import * as DOMUtils from "history/DOMUtils";
import type { UscLocalStorageParams } from "./localStorage";
import { getVisitedVehicleIds } from "./localStorage";
import type { DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import { enableUscMonthlyRate } from "./settings";
import { initializeTmefFunction } from "../../../common-deprecated/utils";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import Debug from "../../../common-deprecated/Debug";
import type {
    ExternalPriceExtraCostsType,
    ExternalPricePayloadType,
    ExternalPriceResponseType,
} from "./externalPriceTypes";
import {
    ExternalPriceFuelType,
    ExternalPriceType,
    ExternalPriceValueType,
    ExternalPriceVehicleCategoryMap,
    ExternalPriceVehicleNetwork,
} from "./externalPriceTypes";
import { DF_DELIVERY_COST_KEY, DfEmissionConsumptionTypeCode } from "../../shared-logic/utils/dfConstants";
import { UscEnv, vehicleHasWarranty } from "../../shared-logic/utils/uscUtils";
import type { UscCarDeliveryConfig } from "./carDeliveryUtils";
import { formatPriceIntl } from "../../../common-deprecated/priceUtils";
import type { VehicleType, UscContext } from "../../shared-logic/types/UscCommonTypes";
import { getUscLabel, getUscOptionalLabel } from "./labels";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import type { UscStateType } from "../redux/store";
import { UscPdpFeeBehaviour } from "./constants";
import type { UscAccessoryType, UscPackType } from "./accessoryUtils";

// ----------------------------------------------------------------------
// Helper types
// ----------------------------------------------------------------------

export enum ExternalPriceKey {
    ReservationFee = "ReservationFee",
    DeliveryCost = "DeliveryCost", // This is different from carDelivery as this is basically a cost to "set up the car" (just has weird naming)
    CarDelivery = "HomeDeliveryCost", // This is actual car delivery cost (so to an actual other dealer or address)
    RegistrationCost = "RegistrationCost",
    PollutionTax = "PollutionTax",
    Ved = "VED",
}

// Source: Vehicle-Domain-Master-reference.xlsx on TME Teams:
// https://teams.microsoft.com/_#/files/General?threadId=19%3A5ff4605f354d4755b3b0ef6730257400%40thread.skype&ctx=channel&context=Data%2520Model&rootfolder=%252Fsites%252FUsedCarTechnicalTeam%252FShared%2520Documents%252FGeneral%252FData%2520Model
export const fuelTypeMap: Record<string, ExternalPriceFuelType> = {
    "1": ExternalPriceFuelType.Petrol,
    "2": ExternalPriceFuelType.Diesel,
    "3": ExternalPriceFuelType.Electric,
    "4": ExternalPriceFuelType.Lpg,
    "10": ExternalPriceFuelType.Hydrogen,
    "11": ExternalPriceFuelType.Hybrid,
};

// ----------------------------------------------------------------------
// Helper functions
// ----------------------------------------------------------------------
export const getLocationId = (dealer?: DealerResultType | null, userTaxLocationId?: string | null): string => {
    return userTaxLocationId || dealer?.address.zip.slice(0, 2) || "";
};

/**
 * Determines when an external price should be shown.
 * This is a helper only used during init, if you want to read externalPrice show state during renders refer to Redux (externalPrice.show).
 */
export const showExternalPrice = (
    uscSettings: UscSettingsType,
    localStorageParams: UscLocalStorageParams,
    vehicleForSaleId: string,
    isPdp: boolean,
): boolean => {
    if (!DOMUtils.canUseDOM) {
        throw new Error("[showExternalPrice] Function can only be used when frontend is initialized");
    }
    if (!uscSettings.enableExternalPrice) return false;

    return isPdp && uscSettings.pdpFeeBehaviour === UscPdpFeeBehaviour.Visited
        ? getVisitedVehicleIds(localStorageParams).includes(vehicleForSaleId)
        : true;
};

/**
 * Get the correct value from a ExtraCosts property out of a TMEF response.
 */
export const getExternalPriceExtraCostValue = (
    extraCost: ExternalPriceExtraCostsType,
): ExternalPriceExtraCostsType["Values"][number] | null => {
    // Currently fairly simple but this could get some more logic over time.
    return extraCost.Values[0] || null;
};

/**
 * Create a payload for TMEF calculations.
 * Payload and responses documented at https://cde.toyota-europe.com/confluence/display/UCP/TMEF+integration+UC2
 */
export const getExternalPricePayload = (
    context: UscContext,
    vehicle: VehicleType,
    dealer: DealerResultType | null,
    userTaxLocationId: string | null,
    accessories: UscAccessoryType[],
    packs: UscPackType[],
    carDeliveryConfig: UscCarDeliveryConfig | null,
    brand: "toyota" | "lexus",
): ExternalPricePayloadType => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const currency = ""; // Empty string for now as we dont have this currently. Doesnt seem required either, for now...

    // Create WLTP object which is based on retrieving WLTP emission values from the vehicle.
    const wltpSpecs: ExternalPricePayloadType["TechnicalSpecifications"]["WLTP"] = [];

    const co2WLTPValue = vehicle.product.eco?.co2Emission?.find(
        (co2Emission) => co2Emission.type.code === DfEmissionConsumptionTypeCode.CombinedWltp,
    );
    if (co2WLTPValue) {
        wltpSpecs.push({
            Type: "vehicleIndividual",
            Category: "CO2",
            Value: String(co2WLTPValue.value),
            TestPhaseName: "Combined", // DF contains more phases but we hardcode Combined for now. If this needs to be dynamic we need to further parse the DF data.
        });
    }

    const fuelConsumptionWLTPValue = vehicle.product.eco?.consumption?.find(
        (consumption) => consumption.type.code === DfEmissionConsumptionTypeCode.CombinedWltp,
    );
    if (fuelConsumptionWLTPValue) {
        wltpSpecs.push({
            Type: "vehicleIndividual",
            Category: "FC",
            Value: String(fuelConsumptionWLTPValue.value),
            TestPhaseName: "Combined", // Hardcoded for now, see explanation of CO2 above.
        });
    }

    const tmefPrices: ExternalPricePayloadType["Prices"] = [];

    // Add car base price.
    tmefPrices.push({
        ID: vehicle.id,
        Type: ExternalPriceType.Car,
        Values: [
            {
                Type: ExternalPriceValueType.InclVat,
                Amount:
                    vehicle.promotion?.promotionPrice.sellingPriceInclVAT || vehicle.price?.sellingPriceInclVAT || 0,
                Currency: currency,
            },
            {
                Type: ExternalPriceValueType.ExclVat,
                Amount:
                    vehicle.promotion?.promotionPrice.sellingPriceExclVAT || vehicle.price?.sellingPriceExclVAT || 0,
                Currency: currency,
            },
        ],
    });

    // Add accessory and pack prices.
    // Note these should contain _all_ accessory/pack prices as every accessory/pack price needs to be calculated through TMEF.
    // TMEF adds various installation costs and rounding logic.
    accessories
        ?.filter((accessory) => !!accessory.cashPriceExclVat)
        .forEach((accessory) => {
            tmefPrices.push({
                ID: accessory.id,
                Type: ExternalPriceType.Accessory,
                Values: [
                    {
                        Type: ExternalPriceValueType.ExclVat,
                        Amount: accessory.cashPriceExclVat,
                        Currency: currency,
                    },
                    {
                        Type: ExternalPriceValueType.LabourTime,
                        Amount: accessory.installationTime,
                    },
                ],
            });
        });
    // We let TMEF parse these packs as accessories for now but this can change in the future.
    packs
        ?.filter((pack) => !!pack.cashPriceExclVat)
        .forEach((pack) => {
            tmefPrices.push({
                ID: pack.id,
                Type: ExternalPriceType.Accessory, // We currently always pass 'Accessory'. This might update in the future
                Values: [
                    {
                        Type: ExternalPriceValueType.ExclVat,
                        Amount: pack.cashPriceExclVat,
                        Currency: currency,
                    },
                    {
                        Type: ExternalPriceValueType.LabourTime,
                        Amount: pack.installationTime,
                    },
                ],
            });
        });

    // Add car delivery price.
    // Note TMEF also returns a 'DeliveryCost' ExtraCost, this is different from the car delivery item we add here! (see externalPriceKeys enum for explanation)
    if (carDeliveryConfig) {
        tmefPrices.push({
            ID: vehicle.id,
            Type: ExternalPriceType.CarDelivery,
            Values: [
                {
                    Type: ExternalPriceValueType.InclVat,
                    Amount: carDeliveryConfig.price.cashPriceInclVat || 0,
                    Currency: currency,
                },
            ],
        });
    }

    return {
        Date: `${year}-${month}-${day}`,
        Product: {
            Suffix: {
                SSN: vehicle.product.ssn,
            },
            FuelType: fuelTypeMap[vehicle.product.engine.fuelType.code] || "", // DF has 10 possible fuelTypes but we only map 4.
            VehicleType: "PC", // Hardcoded, no support for commercial in USC yet.
            Hybrid: vehicle.product.engine.hybrid,
            Plugin: vehicle.product.engine.plugin,
        },
        Prices: tmefPrices,
        Vehicle: {
            Category: ExternalPriceVehicleCategoryMap[context],
            Network:
                brand === "toyota"
                    ? ExternalPriceVehicleNetwork.ToyotaNetwork
                    : ExternalPriceVehicleNetwork.LexusNetwork,
            FirstRegistrationDate: vehicle.history?.registrationDate || "",
        },
        Dealer: {
            SubdivisionCode: getLocationId(dealer, userTaxLocationId) || "",
        },
        TechnicalSpecifications: {
            WLTP: wltpSpecs,
            A2P: [
                {
                    // This is hardcoded for now, it might need to become more dynamic over time but that would require DF updates
                    MasterID: "4106a945-9771-4f83-8409-93cbde8932bb",
                    Value: String(vehicle.product.engine.fiscalPower?.value || 0),
                },
            ],
        },
    };
};

/**
 * Retrieve a formatted list of fees.
 *
 * Wrapped in a function with the includedInTotalPrice param for ease of use and readability when used.
 */
export type FeeListType = {
    cashPrice: number; // cashPrice was added because there are other places that require the unformatted cash price.
    formattedPrice: string; // Price to be shown to the end user. Cash or monthly depending on context.
    label: string;
    key: string;
    disclaimer: string | null;
};
export const getFeeListSelector =
    (includedInTotalPrice: "included" | "excluded") =>
    (state: UscStateType): FeeListType[] => {
        const { externalPrice, commonSettings, uscSettings, vehicle, finance, basket } = state;

        // In some cases (TAF Used) the deliveryCost is provided by an external service which TMEF cannot call.
        // When this happens the deliveryCost is part of the DF vehicleValue property.
        const vehicleDeliveryCost = vehicle.result?.vehicleValue.deliveryCost;

        if ((!externalPrice.show && !vehicleDeliveryCost) || !vehicle.result) return [];

        const monthlyEnabled = enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type);

        return uscSettings.externalPriceConfig
            .map((config) => {
                // The localisation should specifically mention vehicle.vehicleValue.deliveryCost, allowing us to properly localise it.
                // We also assume the cost part of the total price (as we don't have TMEF indicating if it is) as that was the TAF requirement at the time of writing.
                // If the logic of when to show/add this changes be sure to also update getTotalPriceSelector.
                if (config.key === DF_DELIVERY_COST_KEY && vehicleDeliveryCost && includedInTotalPrice === "included") {
                    const parsedConfig: FeeListType = {
                        key: "vehicle.vehicleValue.deliveryCost",
                        label: config.label,
                        disclaimer: config.disclaimer || null,
                        cashPrice: vehicleDeliveryCost,
                        formattedPrice: formatPriceIntl(commonSettings, vehicleDeliveryCost).primaryPrice,
                    };
                    return parsedConfig;
                }

                // The Prices response has 2 types of 'additional prices' arrays:
                // - AdditionalInfo: Always excluded from the total price (WLTP for example)
                // - ExtraCosts: Additional pricing items, can be in/excluded from the total price
                // The USC frontend doesnt have a distinction between these so we try to match either of the arrays.

                // AdditionalInfo is always excluded, parse it first.
                if (includedInTotalPrice === "excluded") {
                    const additionalInfoItem = externalPrice.data?.AdditionalInfo?.find(
                        (info) => info.Key === config.key,
                    );
                    if (additionalInfoItem) {
                        const parsedConfig: FeeListType = {
                            key: additionalInfoItem.Key,
                            label: config.label,
                            disclaimer: config.disclaimer || null,
                            cashPrice: additionalInfoItem.Value,
                            formattedPrice: formatPriceIntl(commonSettings, additionalInfoItem.Value).primaryPrice,
                        };
                        return parsedConfig;
                    }
                }

                // Extra costs have their values in an array, but currently this is not relevant for USC (yet).
                const extraCostsItem = (externalPrice.data as ExternalPriceResponseType)?.ExtraCosts?.find(
                    (info) => info.Key === config.key,
                );
                const extraCostsItemValue = extraCostsItem ? getExternalPriceExtraCostValue(extraCostsItem) : null;

                // If the includedInTotalPrice option doesn't match or it doesn't have a price don't parse the values.
                if (
                    !extraCostsItemValue ||
                    (includedInTotalPrice === "included" && !extraCostsItemValue.IncludedInPrice) ||
                    (includedInTotalPrice === "excluded" && !!extraCostsItemValue.IncludedInPrice)
                ) {
                    return null;
                }

                let formattedPrice = "";

                // Try to find a monthly price for the item.
                // Currently this match happens based on "EposCode" field which seems to be an optional field.
                if (monthlyEnabled && basket.activeFinanceOption === "monthly") {
                    const monthlyPrice = finance.costs.find(
                        (cost) => extraCostsItem!.EposCode && cost.externalPriceCode === extraCostsItem!.EposCode,
                    );
                    if (monthlyPrice) {
                        // Monthly price found, use formatted string if available, else use the default monthly rate parsing.
                        formattedPrice = getUscLabel(state, "uscDefaultMonthlyRateFormat").replace(
                            "{price}",
                            monthlyPrice.monthlyPriceFormatted ||
                                formatPriceIntl(commonSettings, monthlyPrice.monthlyPrice).primaryPrice,
                        );
                    }
                }

                // Since Car Delivery gets shown as a service instead of a fee/tax, we return null here.
                // This should get handled using the getServiceListSelector instead.
                if (config.key === ExternalPriceKey.CarDelivery) {
                    return null;
                }

                // Fallback to cash.
                // This currently implies that if a monthly price is not found for an item the cash price will be shown, even if the active finance option is monthly.
                // The Value has a 'Type' property with externalPriceCostTypes.
                // Ignore it for now as it doesnt seem relevant for USC yet...
                if (!formattedPrice) {
                    formattedPrice = formatPriceIntl(commonSettings, extraCostsItemValue.Amount).primaryPrice;
                }

                const parsedConfig: FeeListType = {
                    key: config.key,
                    label: config.label,
                    disclaimer: config.disclaimer || null,
                    cashPrice: extraCostsItemValue.Amount,
                    formattedPrice,
                };

                return parsedConfig;
            })
            .filter((config) => !!config) as FeeListType[];
    };

/**
 * Simple helper function to determine if the frontend TMEF logic is availabe.
 */
export const externalPriceFnIsAvailable = (): boolean => !!window.calculate;

/**
 * Initialize TMEF frontend function.
 */
export const initializeExternalPriceFn = async (
    commonSettings: CommonSettingsType,
    uscSettings: UscSettingsType,
): Promise<{ success: boolean }> => {
    const { country } = commonSettings;
    const { tmefGatewayUrl } = uscSettings;

    try {
        const result = await fetch(
            `${tmefGatewayUrl}/country/${country}/type/prices/formula${
                uscSettings.uscEnv !== UscEnv.Production ? "?environment=test" : ""
            }`,
        );
        const parsedResult = await result.json();

        return initializeTmefFunction(parsedResult.result);
    } catch (ex) {
        Debug.error("Failed initializing TMEF calculation", ex);
    }
    return { success: false };
};

/**
 * Use the client side TMEF logic to calculate the external price result.
 *
 * Make sure to only use this function when initializeExternalPrice was successful.
 */
export const calculateExternalPrice = (payload: ExternalPricePayloadType): ExternalPriceResponseType | null => {
    if (!externalPriceFnIsAvailable()) Debug.error("Calculate function not available");

    try {
        return window.calculate!(payload) as ExternalPriceResponseType;
    } catch (ex) {
        Debug.error("Failed calculating TMEF price", ex);
    }
    return null;
};

/**
 * Returns a formatted list of all services relevant to the current vehicle.
 * This is used to display the services in the summary on the basket, as well as the summary overview.
 */
export const getServicesListSelector =
    (includedInTotalPrice: "included" | "excluded") =>
    (state: UscStateType): FeeListType[] => {
        const { commonSettings, vehicle, carDelivery, externalPrice } = state;

        const hasWarranty = vehicleHasWarranty(vehicle.result);
        const hasDelivery = !!carDelivery.deliveryConfig;

        const services = [
            {
                key: "warranty", // TODO: since warranty is not an external price, we can't use the key from externalPriceKeys - this should be fixed in the future.
                label: getUscLabel(state, "uscBasketServicesStandardWarrantyTitle", {
                    value: vehicle.result?.warranty?.description,
                }),
                disclaimer: getUscOptionalLabel(state, "uscExtrasDisclaimerWarranty"),
                includedInTotalPrice: "included",
            },
            {
                key: ExternalPriceKey.CarDelivery,
                label: getUscLabel(state, "uscBasketServicesCarDeliveryTitle"),
                disclaimer: getUscOptionalLabel(state, "uscExtrasDisclaimerCarDelivery"),
                includedInTotalPrice: "included",
            },
        ];

        const filteredServices = services.filter((service) => service.includedInTotalPrice === includedInTotalPrice);

        const parsedServices = filteredServices.map((service) => {
            if (service.key === "warranty" && hasWarranty) {
                return {
                    key: service.key,
                    label: service.label,
                    disclaimer: service.disclaimer,
                    cashPrice: 0,
                    formattedPrice: getUscLabel(state, "uscBasketServicesIncluded"),
                };
            }

            if (service.key === ExternalPriceKey.CarDelivery && hasDelivery) {
                const extraCostItem = externalPrice.data?.ExtraCosts?.find(
                    (extraCost) => extraCost.Key === ExternalPriceKey.CarDelivery,
                );

                const extraCostItemValue = extraCostItem ? getExternalPriceExtraCostValue(extraCostItem) : null;

                return {
                    key: service.key,
                    label: service.label,
                    disclaimer: service.disclaimer,
                    cashPrice: extraCostItemValue,
                    formattedPrice: extraCostItemValue
                        ? formatPriceIntl(commonSettings, extraCostItemValue.Amount).primaryPrice
                        : "",
                };
            }

            return null;
        });

        return parsedServices.filter((service) => !!service) as FeeListType[];
    };
